import { FileUp, Scale } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";

import { useAddClaimDocument } from "../../api/use-claim-actions";

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Port of states-cta/UploadAssignationToReview.vue — dépôt de l'assignation à relire. */
export function UploadAssignationToReview({ claimId }: { claimId: string }) {
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const addClaimDocument = useAddClaimDocument(claimId);

  function closeModal() {
    setShowModal(false);
    setSelectedFile(null);
  }

  async function submitAssignation() {
    if (!selectedFile) {
      return;
    }
    const content = await readFileAsDataUrl(selectedFile);
    addClaimDocument.mutate({
      name: selectedFile.name,
      document: content,
      mimeType: selectedFile.type,
      claimDocumentType: "LEGAL_SUBPOENA_TO_REVIEW",
    });
    closeModal();
  }

  return (
    <>
      <Dialog
        open={showModal}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            closeModal();
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajouter l'assignation</DialogTitle>
            <DialogDescription>
              Une fois envoyé, une tâche de relecture sera assigné au cabinet d'avocat.
            </DialogDescription>
          </DialogHeader>

          <DialogPanel>
            <label className="inline-flex cursor-pointer items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50">
              <FileUp className="h-5 w-5 text-gray-500" />
              Sélectionner l'assignation
              <input
                type="file"
                accept=".docx,.doc"
                className="hidden"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {selectedFile ? <p className="mt-2 text-sm text-gray-600">{selectedFile.name}</p> : null}
          </DialogPanel>

          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>
              Annuler
            </Button>
            <Button onClick={submitAssignation} disabled={!selectedFile || addClaimDocument.isPending}>
              Envoyer aux avocats
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-center text-base leading-6 font-semibold text-gray-800">
            <Scale className="mr-2 w-5 text-gray-600" />
            Assignation requise
          </div>
          <div className="mt-5">
            <div className="rounded-md border border-blue-200 bg-blue-50 px-6 py-5 sm:flex sm:items-start sm:justify-between">
              <div className="sm:flex sm:items-start">
                <img
                  className="h-10 w-10 rounded-full"
                  src="https://ucarecdn.com/e3e80df2-387a-4bb4-b48c-623273ab7a4d/"
                  alt="Word"
                />
                <div className="mt-3 text-left sm:mt-0 sm:ml-4">
                  <div className="text-sm font-medium text-gray-900">Ajouter l'assignation</div>
                  <div className="mt-1 text-sm text-gray-600 sm:flex sm:items-center">
                    <div>Format word (.docx)</div>
                  </div>
                </div>
              </div>
              <div className="mt-4 sm:mt-0 sm:ml-6 sm:shrink-0">
                <button
                  type="button"
                  onClick={() => setShowModal(true)}
                  className="inline-flex cursor-pointer items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50"
                >
                  <FileUp className="mr-3 h-5 w-5 text-gray-500" />
                  Ajouter
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
